import { FilterOption, FilterField } from "@/components/filter-panel/types";

export interface ItemInfoResult {
  item: FilterOption;
  parentId: string | null;
  siblings: FilterOption[];
}

/**
 * Recursively find an item and its parent/siblings info in the schema options.
 */
export const findItemInfo = (
  options: FilterOption[],
  targetId: string,
  parentId: string | null = null,
): ItemInfoResult | null => {
  if (!options) return null;
  for (const opt of options) {
    const id = String(opt.id);
    if (id === String(targetId)) {
      return { item: opt, parentId, siblings: options };
    }
    const children = opt.children || opt.subTypes;
    if (children && children.length > 0) {
      const found = findItemInfo(children, targetId, id);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Recursively extract all descendant IDs for a given tree item.
 */
export const getAllDescendantIds = (item: FilterOption): string[] => {
  let ids: string[] = [];
  const children = item.children || item.subTypes;
  if (children) {
    children.forEach((child) => {
      const childId = String(child.id);
      ids.push(childId);
      ids = [...ids, ...getAllDescendantIds(child)];
    });
  }
  return ids;
};

/**
 * Find immediate parent option object for a target ID in options tree.
 */
export const findParentOption = (
  options: FilterOption[],
  targetId: string,
): FilterOption | null => {
  const info = findItemInfo(options, targetId);
  if (info && info.parentId) {
    const parentInfo = findItemInfo(options, info.parentId);
    return parentInfo ? parentInfo.item : null;
  }
  return null;
};

/**
 * Expand compacted selection IDs into full tree selection IDs for UI representation.
 */
export const expandSelection = (
  field: FilterField | undefined,
  selectedIds: string[],
): string[] => {
  if (!field || field.type !== "checkbox" || !field.options) {
    return selectedIds;
  }

  const expandedSet = new Set(selectedIds);
  const options = field.options;

  const processExpansion = (items: FilterOption[]) => {
    items.forEach((item) => {
      const itemId = String(item.id);
      const children = item.children || item.subTypes || [];
      const hasChildren = children.length > 0;

      if (hasChildren) {
        processExpansion(children);
        const allChildrenSelected = children.every((c) =>
          expandedSet.has(String(c.id)),
        );
        const parentSelected = expandedSet.has(itemId);

        if (parentSelected || allChildrenSelected) {
          expandedSet.add(itemId);
          if (parentSelected) {
            getAllDescendantIds(item).forEach((id) =>
              expandedSet.add(String(id)),
            );
          }
        }
      }
    });
  };

  processExpansion(options);
  return Array.from(expandedSet);
};

/**
 * Compact full tree selection IDs to minimal parent IDs for clean URL representation.
 */
export const compactSelection = (
  field: FilterField | undefined,
  selectedIds: string[],
): string[] => {
  if (!field || field.type !== "checkbox" || !field.options) {
    return selectedIds;
  }

  const compactedSet = new Set(selectedIds);
  const options = field.options;

  const processCompaction = (items: FilterOption[]) => {
    items.forEach((item) => {
      const itemId = String(item.id);
      const children = item.children || item.subTypes || [];
      if (children.length > 0) {
        processCompaction(children);
        const allChildrenSelected = children.every((c) =>
          compactedSet.has(String(c.id)),
        );
        const parentSelected = compactedSet.has(itemId);

        if (allChildrenSelected || parentSelected) {
          compactedSet.add(itemId);
          children.forEach((c) => {
            const childId = String(c.id);
            compactedSet.delete(childId);
            const childChildren = c.children || c.subTypes;
            if (childChildren) {
              getAllDescendantIds(c).forEach((did) =>
                compactedSet.delete(String(did)),
              );
            }
          });
        }
      }
    });
  };

  processCompaction(options);
  return Array.from(compactedSet);
};

/**
 * Update tree selection IDs based on selection, deselection, or toggle,
 * respecting parent-child relationships and expanding descendants/ancestors.
 */
export const updateTreeSelection = (
  field: FilterField | undefined,
  currentSelectedIds: string[],
  targetId: string,
  mode: "toggle" | "select" | "deselect" = "toggle",
): string[] => {
  if (!field) return currentSelectedIds;
  if (field.type !== "checkbox") {
    return [String(targetId)];
  }

  const current = currentSelectedIds.map(String);
  const cleanedValue = String(targetId).trim();
  const isCurrentlySelected = current.includes(cleanedValue);

  const shouldSelect =
    mode === "select"
      ? true
      : mode === "deselect"
        ? false
        : !isCurrentlySelected;

  const updatedSet = new Set(current);
  const itemInfo = findItemInfo(field.options || [], cleanedValue);

  if (shouldSelect) {
    // SELECTING: select item and all its descendants
    updatedSet.add(cleanedValue);
    if (itemInfo?.item && (itemInfo.item.children || itemInfo.item.subTypes)) {
      getAllDescendantIds(itemInfo.item).forEach((id) =>
        updatedSet.add(String(id)),
      );
    }
  } else {
    // DESELECTING: deselect item, all descendants, and all ancestor parents
    updatedSet.delete(cleanedValue);
    if (itemInfo?.item && (itemInfo.item.children || itemInfo.item.subTypes)) {
      getAllDescendantIds(itemInfo.item).forEach((id) =>
        updatedSet.delete(String(id)),
      );
    }
    if (itemInfo?.parentId) {
      let currParentId: string | null = itemInfo.parentId;
      while (currParentId) {
        updatedSet.delete(String(currParentId));
        const parentInfo = findItemInfo(field.options || [], currParentId);
        currParentId = parentInfo?.parentId ?? null;
      }
    }
  }

  return expandSelection(field, Array.from(updatedSet));
};
